import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Faq from "@/components/Faq";
import EditorialSources from "@/components/EditorialSources";
import PracticalGuideLinks from "@/components/PracticalGuideLinks";
import MentionGenerator from "@/components/MentionGenerator";
import NotFound from "@/pages/NotFound";
import { GUIDES, guideBySlug, guidePath } from "@/data/guides_index";
import { GUIDE_CONTENT } from "@/data/practical_guides";
import { GUIDE_CONTENT_2 } from "@/data/practical_guides_2";
import { COMPANY } from "@/data/company";
import { trackEvent } from "@/lib/analytics";
import { formatDateFr } from "@/lib/format";

const CONTENT = { ...GUIDE_CONTENT, ...GUIDE_CONTENT_2 };
const SITE = COMPANY.url;
const TOOLS = { "mention-generator": MentionGenerator };

const GuideTable = ({ table }) => (
  <div className="my-6 overflow-x-auto border-2 border-ink bg-white" tabIndex={0} role="region" aria-label={table.caption}>
    <table className="w-full min-w-[640px] text-left text-sm leading-relaxed">
      <caption className="sr-only">{table.caption}</caption>
      <thead className="bg-ink text-paper">
        <tr>{table.head.map((h) => <th key={h} scope="col" className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em]">{h}</th>)}</tr>
      </thead>
      <tbody>
        {table.rows.map((row) => (
          <tr key={row[0]} className="border-t border-ink/15 align-top even:bg-smoke/40">
            {row.map((cell, i) => (i === 0 ? <th key={i} scope="row" className="px-4 py-3 font-semibold">{cell}</th> : <td key={i} className="px-4 py-3 text-ink/80">{cell}</td>))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const relatedGuides = (guide) => {
  const others = GUIDES.filter((g) => g.slug !== guide.slug);
  const sameAudience = others.filter((g) => g.audience === guide.audience || g.audience === "tous");
  return [...sameAudience, ...others.filter((g) => !sameAudience.includes(g))].slice(0, 3).map((g) => g.slug);
};

export default function PracticalGuide() {
  const { slug } = useParams();
  const guide = guideBySlug(slug);
  const content = CONTENT[slug];
  if (!guide || !content) return <NotFound />;

  const path = guidePath(slug);
  const jsonLd = [
    {
      "@context": "https://schema.org", "@type": "Article", "@id": `${SITE}${path}#article`,
      headline: guide.h1, description: guide.desc, inLanguage: "fr-FR",
      datePublished: guide.updated, dateModified: guide.updated,
      mainEntityOfPage: { "@id": `${SITE}${path}#webpage` },
      author: { "@id": `${SITE}/#organization` },
      publisher: { "@id": `${SITE}/#organization` },
      image: `${SITE}/og-image.png`,
      citation: content.sources.map((source) => source.url),
    },
    {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Guides décennale", item: `${SITE}/guide` },
        { "@type": "ListItem", position: 3, name: guide.h1, item: `${SITE}${path}` },
      ],
    },
    {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: content.faq.map(({ q, a }) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
    },
  ];

  return (
    <article className="mx-auto max-w-[1200px] px-5 sm:px-8 py-12 lg:py-20" data-testid="practical-guide">
      <Seo title={guide.title} description={guide.desc} path={path} jsonLd={jsonLd} type="article" />
      <nav aria-label="Fil d’Ariane" className="text-sm mb-8 flex flex-wrap gap-2">
        <Link to="/" className="underline">Accueil</Link><span aria-hidden="true">/</span><Link to="/guide" className="underline">Guides décennale</Link>
      </nav>
      <p className="font-mono text-xs uppercase tracking-widest text-accent mb-4">
        Guide pratique · mis à jour le <time dateTime={guide.updated}>{formatDateFr(guide.updated)}</time>
      </p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight max-w-4xl">{guide.h1}</h1>
      <p className="mt-6 text-lg leading-relaxed max-w-3xl text-ink/80">{content.intro}</p>
      <aside className="my-8 bg-white border-l-4 border-accent p-6 max-w-3xl" aria-label="À retenir">
        <h2 className="font-display font-bold text-xl mb-3">À retenir</h2>
        <ul className="list-disc pl-5 space-y-2 text-sm leading-relaxed">{content.takeaways.map((item) => <li key={item}>{item}</li>)}</ul>
      </aside>
      <nav aria-label="Sommaire du guide" className="my-8 border-y border-ink/20 py-6">
        <h2 className="font-bold mb-3">Dans ce guide</h2>
        <ul className="flex flex-wrap gap-x-6 gap-y-3 text-sm">{content.sections.map((section) => <li key={section.id}><a className="underline underline-offset-4 hover:text-accent" href={`#${section.id}`}>{section.title}</a></li>)}</ul>
      </nav>
      {content.sections.map((section) => {
        const Tool = section.tool && TOOLS[section.tool];
        return (
          <section key={section.id} id={section.id} className={`scroll-mt-28 py-6 ${section.table || Tool ? "max-w-5xl" : "max-w-3xl"}`}>
            <h2 className="font-display text-2xl sm:text-3xl font-bold mb-4">{section.title}</h2>
            {section.table && <GuideTable table={section.table} />}
            {(section.paragraphs || []).map((paragraph) => <p key={paragraph} className="mb-4 leading-relaxed text-ink/80 max-w-3xl">{paragraph}</p>)}
            {Tool && <Tool />}
            {section.items && <ul className="list-disc pl-6 space-y-3 leading-relaxed text-ink/80 max-w-3xl">{section.items.map((item) => <li key={item}>{item}</li>)}</ul>}
          </section>
        );
      })}
      <section className="my-10 border-2 border-ink bg-white p-6 sm:p-8">
        <h2 className="font-display text-2xl font-bold mb-3">Faire étudier votre situation</h2>
        <p className="max-w-2xl text-ink/75 leading-relaxed mb-6">Décrivez vos activités et votre situation dans le formulaire. La demande est gratuite et sans engagement ; elle ne vaut ni contrat ni attestation d’assurance.</p>
        <Link to="/devis" onClick={() => trackEvent("guide_quote_click", { guide: slug })} className="inline-flex items-center gap-3 border-2 border-ink bg-accent text-paper px-6 py-4 font-bold hover:bg-ink">Demander mon devis <ArrowRight aria-hidden="true" className="h-4 w-4" /></Link>
      </section>
      <section className="my-12">
        <h2 className="font-display text-3xl font-bold mb-6">Questions fréquentes</h2>
        <Faq items={content.faq} prefix={`guide-${slug}`} />
      </section>
      <EditorialSources sources={content.sources} />
      <nav aria-label="Pour aller plus loin" className="flex flex-wrap gap-4 border-t border-ink/20 py-6">
        {content.related.map((link) => <Link key={link.path} to={link.path} className="underline underline-offset-4 hover:text-accent">{link.label}</Link>)}
      </nav>
      <PracticalGuideLinks slugs={relatedGuides(guide)} title="Autres guides utiles" />
    </article>
  );
}
